;(() => {
  'use strict'

  const GLOBAL_KEY = 'AlborJournalArchive'
  const ROOT_SELECTOR = '[data-ak-journal-archive]'
  const BATCH_SIZE = 8
  const OBSERVER_MARGIN = 500

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].init(document)
    return
  }

  const instances = new WeakMap()

  class JournalArchive {
    constructor(root) {
      this.root = root
      this.grid = root.querySelector('[data-ak-archive-grid]')
      this.sentinel = root.querySelector('[data-ak-archive-sentinel]')
      this.counter = root.querySelector('[data-ak-archive-count]')
      this.status = root.querySelector('[data-ak-archive-status]')
      this.fallbackButton = root.querySelector('[data-ak-archive-fallback]')
      this.endState = root.querySelector('[data-ak-archive-end]')
      this.bufferTemplate = root.querySelector('template[data-ak-archive-buffer]')

      this.sectionId = root.dataset.sectionId || ''
      this.nextUrl = root.dataset.nextUrl || ''
      this.totalKnown = root.dataset.totalKnown === 'true'
      this.total = this.totalKnown ? Number(root.dataset.total || 0) : null

      this.revealThreshold = Number(root.dataset.revealThreshold || 1)

      this.loading = false
      this.observer = null
      this.queue = []
      this.knownIds = new Set()
      this.excludedIds = new Set((root.dataset.excludedIds || '').split(',').filter(Boolean))

      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

      this.handleFallbackClick = this.handleFallbackClick.bind(this)

      if (!this.grid || !this.sentinel || !this.sectionId) {
        return
      }

      this.shown = 0

      Array.from(this.grid.children).forEach((card) => {
        if (this.rememberCard(card)) {
          this.shown += 1
        }
      })

      this.consumeTemplate(this.bufferTemplate)

      this.root.classList.add('is-js')

      if (this.fallbackButton) {
        this.fallbackButton.addEventListener('click', this.handleFallbackClick)
      }

      this.start().catch((error) => {
        this.handleError(error)
      })
    }

    async start() {
      this.updateCounter()

      if (this.root.hidden) {
        this.setBusy(true)

        await this.fillQueue(this.revealThreshold)

        if (this.queue.length < this.revealThreshold && !this.nextUrl) {
          this.removeHiddenArchive()
          return
        }

        await this.fillQueue(BATCH_SIZE)

        if (this.queue.length < this.revealThreshold) {
          this.removeHiddenArchive()
          return
        }

        const initialCards = this.queue.splice(0, Math.min(BATCH_SIZE, this.queue.length))

        this.root.hidden = false
        this.appendCards(initialCards, false)
        this.shown += initialCards.length

        this.updateCounter()
        this.announceLoaded(initialCards.length)
        this.setBusy(false)
      }

      if (this.isComplete()) {
        this.finish()
        return
      }

      this.ensureObserver()
      this.maybeContinue()
    }

    rememberCard(card) {
      if (!card || !card.matches('[data-article-id]')) {
        return false
      }

      const articleId = String(card.dataset.articleId || '')

      if (!articleId || this.excludedIds.has(articleId) || this.knownIds.has(articleId)) {
        return false
      }

      this.knownIds.add(articleId)
      return true
    }

    enqueueCard(card) {
      if (!this.rememberCard(card)) {
        return false
      }

      this.queue.push(card)
      return true
    }

    consumeTemplate(template) {
      if (!template) return 0

      const fragment = template.content.cloneNode(true)
      let added = 0

      Array.from(fragment.children).forEach((card) => {
        if (this.enqueueCard(card)) {
          added += 1
        }
      })

      template.remove()
      return added
    }

    collectResponseCards(responseRoot) {
      let added = 0

      const responseGrid = responseRoot.querySelector('[data-ak-archive-grid]')

      if (responseGrid) {
        Array.from(responseGrid.children).forEach((card) => {
          if (this.enqueueCard(card)) {
            added += 1
          }
        })
      }

      const responseBuffer = responseRoot.querySelector('template[data-ak-archive-buffer]')

      if (responseBuffer) {
        const fragment = responseBuffer.content.cloneNode(true)

        Array.from(fragment.children).forEach((card) => {
          if (this.enqueueCard(card)) {
            added += 1
          }
        })
      }

      return added
    }

    format(template, values) {
      return Object.entries(values).reduce((output, [key, value]) => {
        return output.split(`[${key}]`).join(String(value))
      }, template || '')
    }

    setBusy(isBusy) {
      this.root.setAttribute('aria-busy', String(isBusy))

      this.grid.setAttribute('aria-busy', String(isBusy))
    }

    updateCounter() {
      if (!this.counter || this.shown < 1) {
        return
      }

      const template = this.totalKnown
        ? this.root.dataset.countTemplate
        : this.root.dataset.filteredCountTemplate

      this.counter.textContent = this.format(template, {
        shown: this.shown,
        total: this.totalKnown ? this.total : ''
      })
    }

    announceLoaded(count) {
      if (!this.status) return

      this.status.textContent = this.format(this.root.dataset.statusTemplate, {
        count,
        shown: this.shown,
        total: this.totalKnown ? this.total : ''
      })
    }

    announceError() {
      if (!this.status) return

      this.status.textContent = this.root.dataset.errorMessage || ''
    }

    showFallback() {
      if (!this.fallbackButton || this.root.hidden || this.isComplete()) {
        return
      }

      let remaining = BATCH_SIZE

      if (this.totalKnown) {
        remaining = Math.min(BATCH_SIZE, this.total - this.shown)
      } else if (!this.nextUrl) {
        remaining = Math.min(BATCH_SIZE, this.queue.length)
      }

      this.fallbackButton.hidden = false
      this.fallbackButton.disabled = false

      this.fallbackButton.textContent = this.format(this.root.dataset.moreTemplate, {
        count: remaining
      })
    }

    hideFallback() {
      if (this.fallbackButton) {
        this.fallbackButton.hidden = true
      }
    }

    ensureObserver() {
      if (this.observer || this.isComplete()) {
        return
      }

      if (!('IntersectionObserver' in window)) {
        this.showFallback()
        return
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0]

          if (entry && entry.isIntersecting) {
            this.loadNextBatch()
          }
        },
        {
          rootMargin: `${OBSERVER_MARGIN}px 0px`
        }
      )

      this.observer.observe(this.sentinel)
    }

    disconnectObserver() {
      if (!this.observer) return

      this.observer.disconnect()
      this.observer = null
    }

    addSkeletons(count) {
      if (count < 1 || this.root.hidden) {
        return null
      }

      const wrapper = document.createElement('div')
      wrapper.style.display = 'contents'
      wrapper.dataset.akArchiveSkeletons = ''

      for (let index = 0; index < count; index += 1) {
        const skeleton = document.createElement('div')
        skeleton.className = 'ak-journal-skeleton'
        skeleton.setAttribute('aria-hidden', 'true')

        skeleton.innerHTML = `
          <div class="ak-journal-skeleton__media"></div>
          <div class="ak-journal-skeleton__line ak-journal-skeleton__line--tag"></div>
          <div class="ak-journal-skeleton__line ak-journal-skeleton__line--title-a"></div>
          <div class="ak-journal-skeleton__line ak-journal-skeleton__line--title-b"></div>
          <div class="ak-journal-skeleton__line ak-journal-skeleton__line--excerpt"></div>
        `

        wrapper.appendChild(skeleton)
      }

      this.grid.appendChild(wrapper)
      return wrapper
    }

    async fetchNextPage() {
      if (!this.nextUrl) return 0

      const requestedUrl = this.nextUrl

      const url = new URL(requestedUrl, window.location.origin)

      url.searchParams.set('section_id', this.sectionId)

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        throw new Error(`Archive request failed with ${response.status}`)
      }

      const html = await response.text()

      const documentFragment = new DOMParser().parseFromString(html, 'text/html')

      const responseRoot = documentFragment.querySelector(ROOT_SELECTOR)

      if (!responseRoot) {
        throw new Error('Archive section was not found in the response.')
      }

      this.nextUrl = responseRoot.dataset.nextUrl || ''

      this.root.dataset.nextUrl = this.nextUrl

      const added = this.collectResponseCards(responseRoot)

      if (added === 0 && this.nextUrl === requestedUrl) {
        this.nextUrl = ''
        this.root.dataset.nextUrl = ''
      }

      return added
    }

    async fillQueue(minimum) {
      let safetyCounter = 0

      while (this.queue.length < minimum && this.nextUrl && safetyCounter < 100) {
        const queueLengthBefore = this.queue.length
        const urlBefore = this.nextUrl

        await this.fetchNextPage()

        safetyCounter += 1

        if (this.queue.length === queueLengthBefore && this.nextUrl === urlBefore) {
          this.nextUrl = ''
          this.root.dataset.nextUrl = ''
          break
        }
      }
    }

    appendCards(cards, animate = true) {
      cards.forEach((card, index) => {
        if (animate && !this.reduceMotion) {
          card.classList.add('is-entering')

          card.style.setProperty('--ak-journal-stagger', `${index * 45}ms`)
        }

        this.grid.appendChild(card)
      })

      if (!animate || this.reduceMotion) {
        return
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          cards.forEach((card) => {
            card.classList.add('is-entered')
          })
        })
      })
    }

    isComplete() {
      if (this.totalKnown) {
        return this.shown >= this.total
      }

      return !this.nextUrl && this.queue.length === 0
    }

    maybeContinue() {
      window.setTimeout(() => {
        if (this.loading || this.isComplete() || this.root.hidden) {
          return
        }

        const bounds = this.sentinel.getBoundingClientRect()

        if (bounds.top < window.innerHeight + OBSERVER_MARGIN) {
          this.loadNextBatch()
        }
      }, 80)
    }

    async loadNextBatch(manual = false) {
      if (this.loading || this.isComplete() || this.root.hidden) {
        return
      }

      this.loading = true
      this.setBusy(true)
      this.hideFallback()

      let desired = BATCH_SIZE

      if (this.totalKnown) {
        desired = Math.min(BATCH_SIZE, this.total - this.shown)
      }

      let skeletons = null

      try {
        if (this.queue.length < desired) {
          skeletons = this.addSkeletons(desired)

          await this.fillQueue(desired)
        }

        if (skeletons) {
          skeletons.remove()
          skeletons = null
        }

        const available = Math.min(desired, this.queue.length)

        const nextCards = this.queue.splice(0, available)

        if (nextCards.length === 0) {
          this.finish()
          return
        }

        this.appendCards(nextCards, true)
        this.shown += nextCards.length

        this.updateCounter()
        this.announceLoaded(nextCards.length)

        if (this.isComplete()) {
          this.finish()
        } else {
          this.ensureObserver()

          if (!manual) {
            this.maybeContinue()
          }
        }
      } catch (error) {
        if (skeletons) {
          skeletons.remove()
        }

        this.handleError(error)
      } finally {
        this.loading = false
        this.setBusy(false)
      }
    }

    handleFallbackClick() {
      this.loadNextBatch(true)
    }

    handleError(error) {
      console.error('[Albor Journal] Archive loading error:', error)

      this.disconnectObserver()
      this.announceError()

      if (this.root.hidden) {
        this.removeHiddenArchive()
      } else {
        this.showFallback()
      }
    }

    removeHiddenArchive() {
      this.disconnectObserver()
      this.setBusy(false)
      this.root.remove()
    }

    finish() {
      this.disconnectObserver()
      this.hideFallback()
      this.updateCounter()

      if (!this.root.hidden && this.endState) {
        this.endState.hidden = false
      }
    }

    destroy() {
      this.disconnectObserver()

      if (this.fallbackButton) {
        this.fallbackButton.removeEventListener('click', this.handleFallbackClick)
      }

      instances.delete(this.root)
    }
  }

  function findRoots(scope) {
    const roots = []

    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) {
      roots.push(scope)
    }

    if (scope.querySelectorAll) {
      roots.push(...scope.querySelectorAll(ROOT_SELECTOR))
    }

    return roots
  }

  function init(scope = document) {
    findRoots(scope).forEach((root) => {
      if (instances.has(root)) return

      const instance = new JournalArchive(root)
      instances.set(root, instance)
    })
  }

  function destroy(scope) {
    findRoots(scope).forEach((root) => {
      const instance = instances.get(root)

      if (instance) {
        instance.destroy()
      }
    })
  }

  window[GLOBAL_KEY] = {
    init,
    destroy
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document))
  } else {
    init(document)
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target))

  document.addEventListener('shopify:section:unload', (event) => destroy(event.target))
})()
